// material-ui
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    Checkbox,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    ImageList
} from '@mui/material';
import { deepPurple, lime } from '@mui/material/colors';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import { useState, useEffect } from 'react';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { successAlert, warningAlert, errorAlert } from 'ui-component/Toasts';
import { useTranslation } from "react-i18next";
import SingleImageDialog from './SingleImageDialog';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1000,
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const KYCVerificationModal = ({
    kycVerificationModalOpen,
    setKYCVerificationModalOpen,
    customerRow,
    handleRefresh
}) => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);

    const [loading, setLoading] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [rejectLoading, setRejectLoading] = useState(false);

    const [customerID, setCustomerID] = useState(0);
    const [rejectReason, setRejectReason] = useState('');
    
    useEffect(() => {
        if (kycVerificationModalOpen && customerRow?.id) {
            setCustomerID(customerRow.id);
            fetchKYCVerification(customerRow.id);
            fetchKYCVerificationRejectReasons();
        }
    }, [kycVerificationModalOpen, customerRow]);

    const [kycVerification, setKYCVerification] = useState();
    const fetchKYCVerification = async (passedCustomerID) => {
        try {
            setLoading(true);

            let apiCustomerID = !passedCustomerID ? customerID : passedCustomerID;

            let url = `/Backend/Customer/KYCVerification?customerID=${apiCustomerID}`;
            
            const res = await axiosInstance.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (res.status === 200) {
                setKYCVerification(res.data);
            }
        } catch (error) {

        }
        finally {
            setLoading(false);
        }
    };

    const [kycVerificationRejectReasons, setKYCVerificationRejectReasons] = useState([]);
    const fetchKYCVerificationRejectReasons = async () => {
        try {
            let url = `/KYCVerificationRejectReasons/All`;
            
            const res = await axiosInstance.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            if (res.status === 200) {
                setKYCVerificationRejectReasons(res.data);
            }
        } catch (error) {

        }
        finally {
            setLoading(false);
        }
    };

    const approveKYCVerification = async () => {
        setApproveLoading(true);
        try {
            const res = await axiosInstance.put(
                '/KYCVerification/Approve',
                {
                    id: kycVerification ? kycVerification.id : 0,
                    customerID: kycVerification ? kycVerification.customerID : 0
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (res.status === 200) {
                successAlert('Succesfully Approve KYC Verification');
                setKYCVerificationModalOpen(false);
                handleRefresh();
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setApproveLoading(false);
        }
    };

    const rejectKYCVerification = async () => {
        setRejectLoading(true);
        try {
            const res = await axiosInstance.put(
                '/KYCVerification/Reject',
                {
                    id: kycVerification ? kycVerification.id : 0,
                    customerID: kycVerification ? kycVerification.customerID : 0,
                    rejectReasonID: rejectReason ? parseInt(rejectReason) : 0
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (res.status === 200) {
                successAlert('Succesfully Reject KYC Verification');
                setKYCVerificationModalOpen(false);
                handleRefresh();
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setRejectLoading(false);
        }
    };

    return (
        <div>
            <Button
                onClick={() => setKYCVerificationModalOpen(true)}
                sx={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: 'fit-content',
                    height: 'fit-content',
                    color: deepPurple[500]
                }}
            >
                <AttachMoneyIcon />
            </Button>
            <Modal open={kycVerificationModalOpen} onClose={() => setKYCVerificationModalOpen(false)} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={style}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bolder' }}>
                            {t("customer.kycVerification")}
                        </Box>
                        <Grid item container pr={{ md: '5px' }}>
                            <Grid key='kycVerificationTypeNameTitle' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {t("customer.kycVerificationType")} : 
                                </Box>
                            </Grid>
                            <Grid key='kycVerificationTypeName' xs={7} sx={{ mt: 2 }}>
                                <TextField
                                    required
                                    name="kycVerificationTypeName"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={kycVerification ? kycVerification.typeName : ''}
                                    disabled={true}
                                    style={{backgroundColor: 'grey'}}
                                />
                            </Grid>
                        </Grid>
                        <Grid item container pr={{ md: '5px' }}>
                            <Grid key='fullNameTitle' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {t("customer.fullName")} : 
                                </Box>
                            </Grid>
                            <Grid key='fullName' xs={7} sx={{ mt: 2 }}>
                                <TextField
                                    required
                                    name="fullName"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={kycVerification ? kycVerification.fullName : ''}
                                    disabled={true}
                                    style={{backgroundColor: 'grey'}}
                                />
                            </Grid>
                        </Grid>
                        <Grid item container pr={{ md: '5px' }}>
                            <Grid key='idNumberTitle' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {t("customer.idNumber")} : 
                                </Box>
                            </Grid>
                            <Grid key='idNumber' xs={7} sx={{ mt: 2 }}>
                                <TextField
                                    required
                                    name="idNumber"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    value={kycVerification ? kycVerification.idNumber : ''}
                                    disabled={true}
                                    style={{backgroundColor: 'grey'}}
                                />
                            </Grid>
                        </Grid>
                        <Grid item container pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {kycVerification?.imageUrl?.length > 0 && (
                                    <ImageList sx={{ width: '100%' }} cols={3} rowHeight={'auto'}>
                                        {kycVerification?.imageUrl?.map((item, index) => (
                                            <SingleImageDialog imageSrc={item} key={index} />
                                        ))}
                                    </ImageList>
                                )}
                            </Box>
                        </Grid>
                        <Grid item container pr={{ md: '5px' }}>
                            <Grid key='rejectReasonTitle' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {t("customer.rejectReason")} : 
                                </Box>
                            </Grid>
                            <Grid key='rejectReason' xs={7} sx={{ mt: 2 }}>
                                <Select
                                    displayEmpty
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                >
                                    <MenuItem sx={{ width: '100%' }} value="">
                                        {t("customer.selectRejectReason")}
                                    </MenuItem>
                                    {kycVerificationRejectReasons.map((item, index) => (
                                        <MenuItem sx={{ width: '100%' }} value={item.id} key={index}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                        </Grid>
                        <Box style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                            <Button
                                variant="contained"
                                sx={{
                                    display: 'block',
                                    mx: 'auto',
                                    width: '150px',
                                    backgroundColor: 'green',
                                    marginTop: '30px'
                                }}
                                onClick={approveKYCVerification}
                                disabled={approveLoading}
                            >
                                {t("withdrawal.approve")}
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    display: 'block',
                                    mx: 'auto',
                                    width: '150px',
                                    backgroundColor: 'red',
                                    marginTop: '30px'
                                }}
                                onClick={() => rejectKYCVerification()}
                                disabled={rejectLoading}
                            >
                                {t("withdrawal.reject")}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Modal>
        </div>
    );
};

export default KYCVerificationModal;
